import { useState } from 'react';
import { useLocalData } from '../hooks/useLocalData';
import { localDataItemService, LocalDataItem } from '../services/localDataService';
import './Screen.css';

export default function DataManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', status: 'active' });

  // Use local data service (no AWS needed)
  const { data, loading, refetch } = useLocalData<LocalDataItem>('dataItems');

  const filteredData = data.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleCreate = async () => {
    try {
      await localDataItemService.create({
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setShowCreateModal(false);
      setFormData({ name: '', description: '', status: 'active' });
      refetch();
    } catch (error) {
      console.error('Failed to create item:', error);
    }
  };

  const handleUpdate = async (id: string, updates: Partial<LocalDataItem>) => {
    try {
      await localDataItemService.update(id, updates);
      refetch();
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await localDataItemService.delete(id);
        refetch();
      } catch (error) {
        console.error('Failed to delete item:', error);
      }
    }
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>Data Management</h1>
        <p className="screen-subtitle">Manage your data items</p>
      </div>

      <div className="toolbar">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          + Create New
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading data...</div>
      ) : (
        <div className="data-grid">
          {filteredData.length > 0 ? (
            filteredData.map((item) => (
              <div key={item.id} className="data-card">
                <div className="card-header">
                  <h3>{item.name}</h3>
                  <span className={`status-badge status-${item.status}`}>
                    {item.status}
                  </span>
                </div>
                <p className="card-description">{item.description}</p>
                <div className="card-footer">
                  <span className="card-meta">
                    Updated: {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : 'N/A'}
                  </span>
                  <div className="card-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleUpdate(item.id, { status: item.status === 'active' ? 'inactive' : 'active' })}
                      title="Toggle status"
                    >
                      🔄
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDelete(item.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No data items found</p>
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Item</h2>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-input"
                rows={4}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="form-input"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCreate}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

